import React, { useState, useRef, useEffect } from "react";
import { Pencil as EditIcon, Plus as AddIcon, MoreHorizontal, Lock, Unlock } from "lucide-react";

const PhaseHeader = ({
  phaseGroup,
  isLoading,
  openDropdownPhaseId,
  setOpenDropdownPhaseId,
  openEditDropdownPhaseId,
  setOpenEditDropdownPhaseId,
  phaseDropdownRef,
  onExecuteRules,
  onCreateBracket,
  onCreateGroup,
  onCreateMatch,
  onEditPhase,
  onDeletePhase,
  onRenamePhase,
  onAutoAssignAll,
  onTogglePhaseLock,
  isOwner = false,
  canEditFormat = true,
}) => {
  const hasGroupBlocks = phaseGroup.groups?.some((b) => b.type === "GROUP");

  // Inline name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(phaseGroup.name);
  const nameInputRef = useRef(null);

  useEffect(() => {
    setEditName(phaseGroup.name);
  }, [phaseGroup.name]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameSubmit = () => {
    setIsEditingName(false);
    const trimmed = editName.trim();
    if (trimmed && trimmed !== phaseGroup.name) {
      onRenamePhase?.(phaseGroup.id, trimmed);
    } else {
      setEditName(phaseGroup.name);
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleNameSubmit();
    } else if (e.key === "Escape") {
      setEditName(phaseGroup.name);
      setIsEditingName(false);
    }
  };

  const handleOpenEditPhase = () => {
    setOpenEditDropdownPhaseId(null);

    const existingMatchRules = phaseGroup.matchRules || {
      gameDuration: "30",
      breaks: "1",
      breakDuration: "5",
      intervalBetweenMatches: "5",
      pointsForWin: "0",
      pointsForDraw: "0",
      pointsForLoss: "0",
    };

    const { pointsForForfeit, ...rawMatchRules } = existingMatchRules || {};

    const sanitizedMatchRules = {
      gameDuration: String(rawMatchRules.gameDuration || 30),
      breaks: String(rawMatchRules.breaks || 1),
      breakDuration: String(rawMatchRules.breakDuration || 5),
      intervalBetweenMatches: String(rawMatchRules.intervalBetweenMatches || 5),
      pointsForWin: String(rawMatchRules.pointsForWin || 0),
      pointsForDraw: String(rawMatchRules.pointsForDraw || 0),
      pointsForLoss: String(rawMatchRules.pointsForLoss || 0),
    };

    onEditPhase(phaseGroup, {
      name: phaseGroup.name,
      inheritMatchRules: false,
      inheritMatchSchedule: false,
      useSingleStartTime: !phaseGroup.followMatchTimings,
      availablePitches: phaseGroup.availablePitches || [],
      startAfterPreviousPhase: phaseGroup.startAfterPreviousPhase || false,
      matchRules: sanitizedMatchRules,
      matchSchedule: {
        startDate: phaseGroup.matchSchedule?.startDate || "",
        endDate: phaseGroup.matchSchedule?.endDate || "",
        matchDays: phaseGroup.matchSchedule?.matchDays || [],
        matchTimings: phaseGroup.matchSchedule?.matchTimings || [],
        startTime: phaseGroup.matchSchedule?.startTime || "",
        dayEndTime: phaseGroup.matchSchedule?.dayEndTime || "",
      },
    });
  };

  return (
    <div className="bg-blue-shade-8 px-[16px] py-[8px] rounded-[16px] flex justify-between items-center">
      <div className="flex items-center">
        {isEditingName ? (
          <input
            ref={nameInputRef}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={handleNameKeyDown}
            style={{ width: `${Math.max(editName.length, 1)}ch` }}
            className="text-[16px] font-semibold text-app-text bg-transparent outline-none"
          />
        ) : (
          <>
            <h2
              className={`text-[16px] font-semibold mr-2 text-app-text-secondary ${canEditFormat ? "cursor-text hover:text-[#FEFCEB] transition-colors" : ""}`}
              onClick={() => canEditFormat && setIsEditingName(true)}
            >
              {editName}
            </h2>
            {canEditFormat && (
              <button
                type="button"
                aria-label="Edit stage name"
                onClick={() => setIsEditingName(true)}
                className="p-0.5 mr-1 text-app-text-secondary hover:text-[#FEFCEB] transition-colors cursor-pointer"
              >
                <EditIcon size={12} />
              </button>
            )}
          </>
        )}
        {phaseGroup.locked && (
          <Lock size={14} className="text-yellow-500 mr-1" title="Stage is locked" />
        )}
      </div>

      <div className="flex ml-5 items-center relative" data-dropdown-id={phaseGroup.id}>

        {canEditFormat && (
          <>
            {/* Add button */}
            <button
              data-phase-id={phaseGroup.id}
              className="bg-transparent border-none p-1 cursor-pointer text-app-text-secondary hover:bg-app-border rounded-full transition-all flex items-center justify-center"
              onClick={() =>
                setOpenDropdownPhaseId(
                  openDropdownPhaseId === phaseGroup.id ? null : phaseGroup.id
                )
              }
            >
              <AddIcon size={24} />
            </button>

            {/* Edit button */}
            <button
              data-phase-id={phaseGroup.id}
              className="bg-transparent border-none p-1 cursor-pointer text-app-text-secondary hover:bg-app-border rounded-full transition-all flex items-center justify-center"
              onClick={() => {
                setOpenEditDropdownPhaseId(
                  openEditDropdownPhaseId === phaseGroup.id ? null : phaseGroup.id
                );
              }}
            >
                <MoreHorizontal size={24} />
            </button>
          </>
        )}

        {/* Add Dropdown Menu */}
        {openDropdownPhaseId === phaseGroup.id && (
          <div
            className="absolute top-full right-0 mt-2 w-[180px] bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl overflow-hidden z-[1000]"
            ref={phaseDropdownRef}
          >
            <button
              className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
              onClick={() => {
                setOpenDropdownPhaseId(null);
                onCreateBracket(phaseGroup);
              }}
            >
              Bracket
            </button>
            <button
              className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
              onClick={() => {
                setOpenDropdownPhaseId(null);
                onCreateGroup(phaseGroup);
              }}
            >
              Group
            </button>
            <button
              className="w-full text-left px-4 py-3 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer transition-colors"
              onClick={() => {
                setOpenDropdownPhaseId(null);
                onCreateMatch(phaseGroup);
              }}
            >
              Match
            </button>
          </div>
        )}

        {/* Edit Dropdown Menu */}
        {openEditDropdownPhaseId === phaseGroup.id && (
          <div
            className="absolute top-full right-0 mt-2 w-[200px] bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl overflow-hidden z-[1000]"
            ref={phaseDropdownRef}
          >
            {hasGroupBlocks && (
              <button
                className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                onClick={handleOpenEditPhase}
              >
                Edit points system
              </button>
            )}
            {hasGroupBlocks && (
              <button
                className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-[#FEFCEB] hover:bg-[#0D1B29] cursor-pointer border-b border-[#192634] transition-colors"
                onClick={() => {
                  setOpenEditDropdownPhaseId(null);
                  onAutoAssignAll(phaseGroup.id);
                }}
              >
                Auto assign teams
              </button>
            )}
            <button
              className="w-full text-left px-4 py-3 pr-12 text-sm font-medium text-red-400 hover:bg-red-500/20 hover:text-red-300 cursor-pointer transition-colors"
              onClick={() => {
                setOpenEditDropdownPhaseId(null);
                onDeletePhase(phaseGroup.id);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhaseHeader;
